// I am using this code for both website and app
import type { ModuleMarkerSource } from "../lib/module-art.ts";
import { isGuardianModule, isMercenaryGear } from "../lib/module-art.ts";

interface ModulePropertyMarkersProps {
  module: ModuleMarkerSource | null | undefined;
  iconDirectory?: string;
  thargoidIconPath?: string;
  className?: string;
}

const powerplayModules: Record<string, { faction: string; icon: string }> = {
  "prismatic shield generator": { faction: "Aisling Duval", icon: "aisling-duval" },
  "pack-hound missile rack": { faction: "Archon Delaine", icon: "archon-delaine" },
  "imperial hammer rail gun": { faction: "Arissa Lavigny-Duval", icon: "arissa-lavigny-duval" },
  "pacifier frag-cannon": { faction: "Denton Patreus", icon: "denton-patreus" },
  "mining lance": { faction: "Edmund Mahon", icon: "edmund-mahon" },
  "retributor beam laser": { faction: "Felicia Winters", icon: "felicia-winters" },
  "concord cannon": { faction: "Pranav Antal", icon: "pranav-antal" },
  "cytoscrambler burst laser": { faction: "Yuri Grom", icon: "yuri-grom" },
  "enforcer cannon": { faction: "Zachary Hudson", icon: "zachary-hudson" },
  "advanced plasma accelerator": { faction: "Zemina Torval", icon: "zemina-torval" },
};

function mountMarker(module: ModuleMarkerSource): { src: string; label: string } | null {
  if (module.mount === "Fixed") return { src: "/ed-icons/weapon types/ed-fixed.svg", label: "Fixed mount" };
  if (module.mount === "Gimballed") return { src: "/ed-icons/weapon types/ed-gimballed.svg", label: "Gimballed mount" };
  if (module.mount === "Turreted") return { src: "/ed-icons/weapon types/ed-turret.svg", label: "Turreted mount" };
  return null;
}

const baseDamageTypes: Record<string, "kinetic" | "thermal" | "explosive"> = {
  "multi-cannon": "kinetic", "cannon": "kinetic", "fragment cannon": "kinetic",
  "beam laser": "thermal", "burst laser": "thermal", "pulse laser": "thermal",
  "seeker missile rack": "explosive", "missile rack": "explosive", "torpedo pylon": "explosive",
};

export function ModulePropertyMarkers({
  module,
  iconDirectory = "/ed-icons",
  thargoidIconPath = "/ed-icons/codex/thargoid-codex.svg",
  className = "",
}: ModulePropertyMarkersProps) {
  if (!module) return null;

  const mount = mountMarker(module);
  const damage = !module.preEngineered && module.category === "hardpoint"
    ? baseDamageTypes[module.name?.trim().toLowerCase() ?? ""] : undefined;
  const guardian = isGuardianModule(module);
  const powerplay = powerplayModules[module.name?.trim().toLowerCase() ?? ""];
  const mercenaryGear = isMercenaryGear(module);
  const mercCoin = module.purchaseCurrency === "merc-coin";
  const preEngineered = Boolean(module.preEngineered);
  const thargoid = /thargoid/i.test(`${module.name ?? ""} ${module.symbol ?? ""}`);

  if (!mount && !damage && !guardian && !powerplay && !mercenaryGear && !mercCoin && !preEngineered && !thargoid) return null;

  return (
    <span className={`module-property-markers${className ? ` ${className}` : ""}`} aria-label="Module properties">
      {mount ? <img src={mount.src} alt={mount.label} title={mount.label} /> : null}
      {damage ? <img src={`/ed-icons/weapon types/${damage}.svg`} alt={`Base damage: ${damage}`} title={`Base damage: ${damage}; experimental effects may change damage type`} /> : null}
      {guardian ? (
        <img
          src={`${iconDirectory}/Guardian_icon.webp`}
          alt="Guardian technology"
          title="Guardian technology"
        />
      ) : null}
      {powerplay ? (
        <img
          className="module-property-powerplay"
          src={`/ed-icons/powers/${powerplay.icon}.svg`}
          alt={`Powerplay module: ${powerplay.faction}`}
          title={`Powerplay module: ${powerplay.faction}`}
        />
      ) : null}
      {thargoid ? <img src={thargoidIconPath} alt="Thargoid technology" title="Thargoid technology" /> : null}
      {mercCoin || mercenaryGear ? (
        <img
          src="/ed-icons/merc-coin-icon.png"
          alt={mercCoin ? "Bought with Merc coin" : "Mercenary gear"}
          title={mercCoin ? "Bought with Merc coin" : "Mercenary gear"}
        />
      ) : null}
      {preEngineered ? <img src="/ed-icons/engineericon.png" alt="Pre-engineered" title="Pre-engineered" /> : null}
    </span>
  );
}
